/**
 * Date and Time Utilities
 * Provides consistent date/time formatting functions that respect user's local timezone
 */

/**
 * Format a date/time string to display in user's local timezone
 * @param {string|Date} dateString - The date string or Date object to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date/time string
 */
export const formatDateTime = (dateString, options = {}) => {
  if (!dateString) return '-';
  
  let date;
  
  // Handle MySQL DATETIME format (YYYY-MM-DD HH:MM:SS)
  // These values are stored in UTC in the database
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
    // Add 'Z' to indicate this is UTC time, so JavaScript converts it properly to local
    const convertedString = dateString.replace(' ', 'T') + 'Z';
    date = new Date(convertedString);
  } else {
    // Handle ISO strings and other formats
    date = new Date(dateString);
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  };
  
  const finalOptions = { ...defaultOptions, ...options };
  
  return date.toLocaleString(undefined, finalOptions);
};

/**
 * Format a date to display only the date part in user's local timezone
 * @param {string|Date} dateString - The date string or Date object to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return '-';
  
  let date;
  
  // Handle MySQL DATETIME format (YYYY-MM-DD HH:MM:SS)
  // These values are stored in UTC in the database
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
    // Add 'Z' to indicate this is UTC time, so JavaScript converts it properly to local
    date = new Date(dateString.replace(' ', 'T') + 'Z');
  } else {
    // Handle ISO strings and other formats
    date = new Date(dateString);
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };
  
  const finalOptions = { ...defaultOptions, ...options };
  
  return date.toLocaleDateString(undefined, finalOptions);
};

/**
 * Format a time to display only the time part in user's local timezone
 * @param {string|Date} dateString - The date string or Date object to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted time string
 */
export const formatTime = (dateString, options = {}) => {
  if (!dateString) return '-';
  
  let date;
  
  // Handle MySQL DATETIME format (YYYY-MM-DD HH:MM:SS)
  // These values are stored in UTC in the database
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
    // Add 'Z' to indicate this is UTC time, so JavaScript converts it properly to local
    date = new Date(dateString.replace(' ', 'T') + 'Z');
  } else {
    // Handle ISO strings and other formats
    date = new Date(dateString);
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) return 'Invalid Time';
  
  const defaultOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  };
  
  const finalOptions = { ...defaultOptions, ...options };
  
  return date.toLocaleTimeString(undefined, finalOptions);
};

/**
 * Format a date/time for table display (compact format)
 * @param {string|Date} dateString - The date string or Date object to format
 * @returns {string} Formatted date/time string
 */
export const formatDateTimeCompact = (dateString) => {
  return formatDateTime(dateString, {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format a relative time string (e.g., "2 hours ago", "3 days ago")
 * @param {string|Date} dateString - The date string or Date object to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return '-';
  
  const date = new Date(dateString);
  const now = new Date();
  
  // Check if date is valid
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const diffInMs = now - date;
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  
  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  } else {
    // For older dates, show the actual date
    return formatDate(dateString);
  }
};

/**
 * Format duration in seconds to a readable string
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration string
 */
export const formatDuration = (seconds) => {
  if (!seconds) return 'N/A';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${remainingSeconds}s`;
  }
};

/**
 * Get timezone information for display
 * @returns {Object} Timezone information
 */
export const getTimezoneInfo = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = new Date().getTimezoneOffset();
  const offsetHours = Math.abs(Math.floor(offset / 60));
  const offsetMinutes = Math.abs(offset % 60);
  const offsetSign = offset <= 0 ? '+' : '-';
  
  return {
    timezone,
    offset: `UTC${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`,
    name: timezone.split('/').pop().replace('_', ' ')
  };
};

/**
 * Format a date range for display
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {string} Formatted date range
 */
export const formatDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) return '-';
  if (!startDate) return `Until ${formatDate(endDate)}`;
  if (!endDate) return `From ${formatDate(startDate)}`;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // If same day, show date once with time range
  if (start.toDateString() === end.toDateString()) {
    return `${formatDate(startDate)}, ${formatTime(startDate)} - ${formatTime(endDate)}`;
  } else {
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  }
};
